import { Injectable } from '@angular/core';
import { SequenceItem } from '@core/components/sequence-modal/sequence-modal.component';
import { SpinnerService } from '@core/services/spinner.service';
import { AutomationAPI } from '@core/typings/api/automation.typing';
import { DetailUnion, WFLDetailSwitch, WFLSaveSwitch, WflAutomationDetailForUi, WorkflowLevelAdvancementAPI, WorklfowLevelAutomationRuleset } from '@core/typings/api/workflow-level-advancement.typing';
import { WorkflowLevelAutomationAPI } from '@core/typings/api/workflow-level-automation.typing';
import { Automation } from '@core/typings/ui/automation.typing';
import { FormFieldAutomationService } from '@features/form-fields/services/form-field-automation.service';
import { FormLogicService } from '@features/forms/services/form-logic/form-logic.service';
import { ProgramService } from '@features/programs/services/program.service';
import { RuleAutomationService } from '@features/rule-automation/rule-automation.service';
import { APIResultData, DebounceFactory, PaginationOptions, SimpleStringMap } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { uniq } from 'lodash';
import { WorkflowLevelAdvancementResources } from '../workflow-level-advancement.resources';
import { WorkflowLevelAutomationResources } from '../workflow-level-automation.resources';
import { WorkflowLevelAutomationState } from '../workflow-level-automation.state';

@AttachYCState(WorkflowLevelAutomationState)
@Injectable({ providedIn: 'root' })
export class WorkflowLevelAutomationService extends BaseYCService<WorkflowLevelAutomationState> {

   constructor (
    private workflowLevelAutomationResources: WorkflowLevelAutomationResources,
    private workflowLevelAdvancementAutomationResources: WorkflowLevelAdvancementResources,
    private i18n: I18nService,
    private automationService: RuleAutomationService,
    private formLogicService: FormLogicService,
    private programService: ProgramService,
    private formFieldAutomationService: FormFieldAutomationService,
    private spinner: SpinnerService,
    private confirmAndTakeAction: ConfirmAndTakeActionService
  ) {
    super();
  }

  get workflowForms () {
    return this.get('workflowForms');
  }

  get workflowAutomationDetail () {
    return this.get('workflowAutomationDetail');
  }

  get workflowAdvancementDetail () {
    return this.get('workflowAdvancementDetail');
  }

  get assignmentTableDataFactory () {
    return this.get('assignmentTableDataFactory');
  }

  get advancementTableDataFactory () {
    return this.get('advancementTableDataFactory');
  }

  get assignmentHistoryTableDataFactory () {
    return this.get('assignmentHistoryTableDataFactory');
  }

  get routingHistoryTableDataFactory () {
    return this.get('routingHistoryTableDataFactory');
  }

  /**
   * Get the Service to Use
   *
   * @param isAdvancement: Is this for Advancement?
   * @returns the service to use
   */
  getService (isAdvancement: boolean) {
    return isAdvancement ?
      'workflowLevelAdvancementAutomationResources' :
      'workflowLevelAutomationResources';
  }

  /**
   * Get the ID value
   *
   * @param record: Record to evaluate
   * @param isAdvancement: Is this for Advancement?
   * @returns the ID value
   */
  getId (
    record: { workflowLevelAutomationRuleSetId: number } | { workflowLevelRoutingAutomationRuleSetId: number },
    isAdvancement: boolean
  ) {
    if (isAdvancement) {
      return (record as any).workflowLevelRoutingAutomationRuleSetId;
    } else {
      return (record as any).workflowLevelAutomationRuleSetId;
    }
  }

  resolveFactories () {
    this.setInitialTableDataFactory();
    this.setAdvancementTableDataFactory();
    this.setAssignmentHistoryTableDataFactory();
    this.setRoutingHistoryTableDataFactory();
  }

  resetWorkflowObjects (isAdvancement: boolean) {
    this.set('WorkflowObjects', undefined);
    this.getWorkflowObjects(isAdvancement);
  }

  resetViewRulesTableDataFactory (isAdvancement = false) {
    if (isAdvancement) {
      this.advancementTableDataFactory.reset.emit();
    } else {
      this.assignmentTableDataFactory.reset.emit();
    }
  }

  getWorkflowObjects (isAdvancement: boolean) {
    const prop = isAdvancement ? 'WorkflowAdvancementObjects' : 'WorkflowObjects';
    if (!this.get(prop)) {
      const objects: Automation.ObjectConfig[] = [
        this.automationService.getApplicationObject(isAdvancement),
        this.automationService.getApplicantObject(),
        this.automationService.getBudgetObject(),
        this.automationService.getOrganizationObject(),
        this.automationService.getEmployeeSsoObject(),
        isAdvancement ? this.automationService.getAggregateObject() : undefined,
        isAdvancement ? this.automationService.getAwardObject() : undefined,
        isAdvancement ? this.automationService.getDecisionObject() : undefined,
        isAdvancement ? this.automationService.getPaymentObject() : undefined
      ].filter((item) => !!item);

      this.set(prop, objects);
    }

    return this.get(prop);
  }

  async fetchWorkflowLevelForms (
    workflowId: number
  ) {
    if (!this.workflowForms[workflowId]) {
      const forms = await this.workflowLevelAutomationResources.getFormsByWorkflowId(workflowId);
      this.set('workflowForms', {
        ...this.workflowForms,
        [workflowId]: forms
      });
    }
  }

  async doSequenceFetch<C extends boolean> (
    workflowId: number,
    formId: number,
    workflowLevelId: number,
    isAdvancement: C
  ) {
    let data: APIResultData<WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetForUi>|
      APIResultData<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetForUi>;
    if (isAdvancement) {
      data = await this.getAdvancementRuleSetsPaginated(
        {
          returnAll: true,
          rowsPerPage: 0,
          retrieveTotalRecordCount: false,
          orFilterColumns: [],
          pageNumber: 0,
          sortColumns: [{
            columnName: 'sequence',
            sortAscending: true
          }],
          filterColumns: [{
            columnName: 'formId',
            filters: [{
              filterType: 'eq',
              filterValue: formId
            }]
          }, {
            columnName: 'workflowId',
            filters: [{
              filterType: 'eq',
              filterValue: workflowId
            }]
          }, {
            columnName: 'workflowLevelId',
            filters: [{
              filterType: 'eq',
              filterValue: workflowLevelId
            }]
          }]
        }
      );
    } else {
      data = await this.getInitialRuleSetsPaginated({
        returnAll: true,
        rowsPerPage: 0,
        retrieveTotalRecordCount: false,
        orFilterColumns: [],
        pageNumber: 0,
        sortColumns: [{
          columnName: 'sequence',
          sortAscending: true
        }],
        filterColumns: [{
          columnName: 'formId',
          filters: [{
            filterType: 'eq',
            filterValue: formId
          }]
        }, {
          columnName: 'workflowId',
          filters: [{
            filterType: 'eq',
            filterValue: workflowId
          }]
        }]
      });
    }

    return data.records;
  }

  async sequence (
    workflowId: number,
    formId: number,
    results: SequenceItem[],
    nextWorkflowLevelId: number,
    isAdvancement: boolean
  ) {
    await this[this.getService(isAdvancement)].sequence(
      workflowId,
      formId,
      results.map((result) => {
        return {
          sequence: result.sequence,
          [
            isAdvancement ?
              'workflowLevelRoutingAutomationRuleSetId' :
              'workflowLevelAutomationRuleSetId'
          ]: result.id
        };
      }),
      nextWorkflowLevelId
    );
  }

  clearWorkflowLevelForms () {
    this.set('workflowForms', {});
  }

  async fetchDetail<T extends boolean> (
    id: number,
    isAdvancement: T
  ): Promise<WFLDetailSwitch<T>> {
    if (!this.workflowAutomationDetail[id]) {
      const detail = await this[this.getService(isAdvancement)].getRuleSet(id);
      const key = this.getDetailKey(isAdvancement);
      const existing: SimpleStringMap<DetailUnion> = this.get(key);
      this.set(key, {
        ...existing,
        [id]: detail
      });

      await this.fetchWorkflowLevelForms(detail.workflowId);
    }

    return (isAdvancement ?
      this.workflowAdvancementDetail[id] :
      this.workflowAutomationDetail[id]) as WFLDetailSwitch<T>;
  }

  getDetailKey (isAdvancement: boolean) {
    return isAdvancement ? 'workflowAdvancementDetail' : 'workflowAutomationDetail';
  }

  async fetchDetailForTable<T extends boolean> (
    id: number,
    isAdvancement: T
  ): Promise<Automation.RuleSetForDisplay<WflAutomationDetailForUi>> {
    await this.fetchDetail(id, isAdvancement);

    const detail = (isAdvancement ?
      this.workflowAdvancementDetail[id] :
      this.workflowAutomationDetail[id]) as WFLDetailSwitch<T>;
    await this.fetchWorkflowLevelForms(detail.workflowId);

    const formOption = this.workflowForms[detail.workflowId]
      .find(form => form.id === detail.formId);
    await this.formLogicService.fetchFormDetail(formOption.id, formOption.currentRevisionId);
    const formObject = await this.automationService.formToRuleObject(
      this.formLogicService.getFormDetail(formOption.id, formOption.currentRevisionId)
    );
    const singleResponseRefIds = await this.getSingleResponseGmFields(
      detail.workflowId,
      detail.formId
    );
    const gmFieldsObject = isAdvancement ?
      await this.formFieldAutomationService.adaptFieldIdsForAutomation(
        singleResponseRefIds
      ) :
      undefined;
    const workflowObjects = this.getWorkflowObjects(isAdvancement);

    return {
      detail: {
        ...detail,
        id: this.getId(detail, isAdvancement)
      },
      rules: this.automationService.mapAPIRulesToViewForDrilldown(
        detail.rules,
        [
          ...workflowObjects,
          formObject,
          gmFieldsObject
        ]
      )
    };
  }

  resetWorkflowAutomationDetail () {
    this.set('workflowAutomationDetail', {});
    this.set('workflowAdvancementDetail', {});
  }

  async doSaveDetail<T extends boolean> (
    level: WFLSaveSwitch<T>,
    isAdvancement: T,
    doActivate: boolean
  ) {
    const result = await this[this.getService(isAdvancement)].saveLevel(level);
    const id = this.getId(result, isAdvancement);
    this.handleLevelUpdate(id, isAdvancement);
    if (doActivate) {
      await this.changeRuleSetStatus(
        id,
        AutomationAPI.AutomationRuleSetStatus.Enabled,
        isAdvancement
      );
    }
  }

  async saveDetail<T extends boolean> (
    level: WFLSaveSwitch<T>,
    isAdvancement: T,
    doActivate: boolean
  ) {
    const {
      passed
    } = await this.confirmAndTakeAction.genericTakeAction(
      () => this.doSaveDetail(level, isAdvancement, doActivate),
      this.i18n.translate(
        'CONFIG:notificationSuccessfullySavedRuleset',
        {},
        'Successfully saved ruleset'
      ),
      this.i18n.translate(
        'CONFIG:notificationErrorSavingRuleset',
        {},
        'There was an error saving your ruleset'
      )
    );

    return passed;

  }

  async changeRuleSetStatus (
    id: number,
    status: AutomationAPI.AutomationRuleSetStatus,
    isAdvancement: boolean
  ) {
    await this[this.getService(isAdvancement)].changeRuleSetStatus(id, status);
    this.handleLevelUpdate(id, isAdvancement);
  }

  async deleteRuleSet (
    ruleSet: WorklfowLevelAutomationRuleset,
    isAdvancement: boolean
  ) {
    await this[this.getService(isAdvancement)].deleteRuleSet(ruleSet.id);
    this.resetViewRulesTableDataFactory(isAdvancement);
  }

  async rerouteApplication (
    workflowId: number,
    applicationId: number,
    comments: string
  ) {
    await this.workflowLevelAutomationResources.rerouteApplication(
      workflowId,
      applicationId,
      comments
    );

    this.assignmentHistoryTableDataFactory.reset.emit();
  }

  private handleLevelUpdate (
    ruleSetId: number,
    isAdvancement: boolean
  ) {
    if (ruleSetId) {
      const key = isAdvancement ?
        'workflowAdvancementDetail' :
        'workflowAutomationDetail';

      this.set(key, {
        ...this[key],
        [ruleSetId]: null
      });
    }
    this.resetViewRulesTableDataFactory(isAdvancement);
  }

  async adaptPaginationOptions (
    options: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>|
      PaginationOptions<WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetDetailModel>
  ) {
    const programFilterColumn = options.filterColumns
      .find(column => column.columnName === 'programId');
    if (!!programFilterColumn) {
      options.filterColumns = options.filterColumns
        .filter(column => column !== programFilterColumn);
      const programId = programFilterColumn.filters[0].filterValue;
      let program = this.programService.get('programMap')[+programId];
      if (!program) {
        this.spinner.startSpinner();
        await this.programService.getProgram('' + programId);
        this.spinner.stopSpinner();
        program = this.programService.get('programMap')[+programId];
      }

      options.filterColumns = options.filterColumns.concat([{
        columnName: 'formId',
        filters: [{
          filterType: 'eq',
          filterValue: program.defaultFormId
        }]
      }, {
        columnName: 'workflowId',
        filters: [{
          filterType: 'eq',
          filterValue: program.workflowId
        }]
      }]);
    }

    return options;
  }

  setAdvancementTableDataFactory () {
    const factory = DebounceFactory.createSimple(async (
      options: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>
    ) => {
      const data = await this.getAdvancementRuleSetsPaginated(options);

      return {
        success: true,
        data
      };
    });
    this.set('advancementTableDataFactory', factory);
  }

  async getAdvancementRuleSetsPaginated (
    options: PaginationOptions<WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetModel>
  ): Promise<APIResultData<WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetForUi>> {
    options = await this.adaptPaginationOptions(options);
    const data = await this.workflowLevelAdvancementAutomationResources.searchRuleSets(options, true);

    return {
      ...data,
      records: this.adaptAdvancementRulesets(data.records)
    };
  }

  adaptAdvancementRulesets (records: WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetModel[]) {
    return records.map<WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetForUi>((record) => {
      return {
        ...record,
        routeToDisplayName: record.routeToWorkflowLevelName,
        id: record.workflowLevelRoutingAutomationRuleSetId,
        isInvalid: record.workflowLevelToRouteToDisabled
      };
    });
  }

  setInitialTableDataFactory () {
    const factory = DebounceFactory.createSimple(async (
      options: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>
    ) => {
      const data = await this.getInitialRuleSetsPaginated(options);

      return {
        success: true,
        data
      };
    });
    this.set('assignmentTableDataFactory', factory);
  }

  async getInitialRuleSetsPaginated (
    options: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>
  ): Promise<APIResultData<WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetForUi>> {
    options = await this.adaptPaginationOptions(options);
    const data = await this.workflowLevelAutomationResources.searchRuleSets(options, false);

    return {
      ...data,
      records: this.adaptInitialRulesets(data.records)
    };
  }

  adaptInitialRulesets (records: WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel[]) {
    return records.map((record) => {
      return {
        ...record,
        routeToDisplayName: record.workflowLevelName,
        id: record.workflowLevelAutomationRuleSetId,
        isInvalid: record.workflowLevelDisabled
      };
    });
  }

  setAssignmentHistoryTableDataFactory () {
    const factory = DebounceFactory.createSimple((
      options: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationApplication>
    ) => {
      return this.workflowLevelAutomationResources.searchHistory(options);
    });
    this.set('assignmentHistoryTableDataFactory', factory as any);
  }

  setRoutingHistoryTableDataFactory () {
    const factory = DebounceFactory.createSimple((
      options: PaginationOptions<WorkflowLevelAdvancementAPI.WorkflowLevelAutomationApplication>
    ) => {
      return this.workflowLevelAdvancementAutomationResources.searchHistory(options);
    });
    this.set('routingHistoryTableDataFactory', factory as any);
  }

  /**
   * Copy the RuleSet
   *
   * @param id: ID
   * @param isAdvancement: Is this for advancement? 
   * @returns the ID of the newly copied record
   */
  async doCopy<T extends boolean> (
    id: number,
    isAdvancement: T
  ) {
    const detail = await this.fetchDetail(id, isAdvancement);
    const payload = {
      workflowId: detail.workflowId,
      workflowLevelId: detail.workflowLevelId,
      routeToWorkflowLevelId: 'routeToWorkflowLevelId' in detail ?
        detail.routeToWorkflowLevelId :
        undefined,
      name: detail.name + ' ' + this.i18n.translate(
        'common:textCopy',
        {},
        'Copy'
      ),
      description: detail.description,
      applyRulesWithOr: detail.applyRulesWithOr,
      formId: detail.formId,
      rules: detail.rules.map((rule) => {
        return {
          ...rule,
          isRemoved: false,
          automationRuleSetExpressionId: null
        };
      })
    };
    const response = await this[this.getService(isAdvancement)].saveLevel(payload);
    id = this.getId(response, isAdvancement);
    this.handleLevelUpdate(id, true);

    return id;
  }

  /**
   * Handle copying a ruleset
   *
   * @param id: ID to Copy
   * @param isAdvancement: Is this for advancement?
   * @returns the ID of the copied record
   */
  async handleCopy<T extends boolean> (
    id: number,
    isAdvancement: T
  ) {
    const {
      passed,
      endpointResponse
    } = await this.confirmAndTakeAction.genericTakeAction(
      () => this.doCopy(id, isAdvancement),
        this.i18n.translate(
        'CONFIG:textSuccessCopyingTheRule',
        {},
        'Successfully copied the rule'
      ),
      this.i18n.translate(
        'CONFIG:textErrorCopyingTheRule',
        {},
        'There was an error copying the rule'
      )
    );

    return passed ? endpointResponse : null;
  }

  async getSingleResponseGmFields (
    workflowId: number,
    defaultFormId: number
  ): Promise<number[]> {
    if (workflowId && defaultFormId) {
      const ids = await this.workflowLevelAutomationResources.getSingleResponseRefFields(
        workflowId,
        defaultFormId
      );

      return uniq(ids);
    }

    return [];
  }

  /**
   * Changes the ruleset status in bulk
   *
   * @param ids: IDs to activate or deactivate
   * @param status: Status to update to
   * @param isAdvancement: Is this for advancement?
   */
  async doChangeRuleSetStatusBulk (
    ids: number[],
    status: AutomationAPI.AutomationRuleSetStatus,
    isAdvancement: boolean
  ) {
    await this.workflowLevelAutomationResources.changeRuleSetStatusBulk(ids, status, isAdvancement);
    this.resetViewRulesTableDataFactory(isAdvancement);
  }

  /**
   * Activate or Deactivate a Group of WFL Automation Rule Sets
   *
   * @param ids: IDs to activate or deactivate
   * @param status: Status to set
   * @param isAdvancement: Is this advancement (routing)?
   */
  async changeRuleSetStatusBulk (
    ids: number[],
    status: AutomationAPI.AutomationRuleSetStatus,
    isAdvancement: boolean
  ) {
    const isActivate = status === AutomationAPI.AutomationRuleSetStatus.Enabled;
    await this.confirmAndTakeAction.genericTakeAction(
      () => this.doChangeRuleSetStatusBulk(ids, status, isAdvancement),
      this.i18n.translate(
        isActivate ? 'common:textSuccessActivateAutomationRecords' : 'common:textSuccessDeactivateAutomationRecords',
        {},
        isActivate ? 'Successfully activated the automation records' : 'Successfully deactivated the automation records'
      ),
      this.i18n.translate(
        isActivate ? 'common:textErrorActivateAutomationRecords' : 'common:textErrorDeactivateAutomationRecords',
        {},
        isActivate ? 'There was an error activating the automation records' : 'There was an error deactivating the automation records'
      )
    );
  }


}
